import { observable, set, action, computed } from 'mobx';
import { ICountry } from '../../../api/IApiResponses';
import { IAddressDisplaySchemaProperty, IAddressSchemaProperties, LookupData } from '../../../api/JsonSchemaApi';
import { BACKEND_VALIDATION_TYPES, INVOICE_CARRIER_TYPES } from '../../../constants/Constants';
import { FlowType, Intent } from 'paypal-checkout-components';
import getEnvironment from '../../../helpers/getEnvironment';
import { EnvironmentType } from '../../../helpers/getEnvironment';
import { IAddressAttributes } from '../Address';
import GOOGLE_MAP_STYLE from '../../../constants/GoogleMapStylers';
import type { DisplayConfig } from '../../../config/display/DisplayConfig';

export interface IDropdownOption {
  label: string;
  value: string;
}

export interface IState extends IDropdownOption {
  default?: string;
}

export interface IStates {
  [key: string]: IState[];
}

export interface INCCCPaymentItem {
  install?: string;
  label?: string;
  optionId: string;
  transMode: string;
  invalid?: boolean;
}

export interface IInvoiceCarrierTypeOption {
  value: INVOICE_CARRIER_TYPES;
  label: string;
}

export interface IInvoiceDonationLoveCodeOption {
  value: string;
  label: string;
}

export type IFieldDependencies = Partial<Record<keyof IAddressAttributes, Array<IDependentField>>>;

export interface IDependentField {
  fieldName: keyof IAddressAttributes;
  lookupData: LookupData;
}

export interface IPayPalSettings {
  commit: boolean;
  flow: FlowType;
  intent: Intent;
}

export interface IStardustServiceConfig {
  baseUrl: string;
  businessUnitId: string;
  clientId: string;
  environment: string;
}

export interface IAddressDisplaySchema {
  [key: string]: IAddressDisplaySchemaProperty;
}

export enum CONVENIENCE_STORE {
  SAGAWA = 'sagawa',
  FAMILY_MART = 'family-mart',
  SEVEN_ELEVEN = 'seven-eleven',
  HI_LIFE = 'hi-life',
}

export interface IConvenienceStoreProvider {
  name: CONVENIENCE_STORE;
  url: string;
  serviceType: string;
  postUrlMobile: string;
  returnUrl: string;
  postUrlPc: string;
  eShopId: string;
  mobileCvsNameSuffix: string;
  postUrl: string;
  vdrNo: string;
  ecCdcNo: string;
  ecCvs: string;
  cvsName: string;
  isDefault: boolean;
}

export interface IAUPostCollections {
  apiKey: string;
  apiSecret: string;
  gooleMapApiKey: string;
  jsPath: string;
  root: string;
  merchantId: string;
  allowedCountries?: number[];
}

export interface IDisplayOurGuaranteeConfig {
  deliveryPage: boolean;
  paymentPage: boolean;
}

export interface IBackendValidation {
  shouldValidateSavedShippingAddress: number;
  validationType: BACKEND_VALIDATION_TYPES;
  countryId?: number;
}

export interface IPhonePattern {
  country: string;
  countryCallingCode: string;
  pattern: string;
  formatter?: string;
}

export interface IPhoneCountryPattern {
  [country_code: string]: IPhonePattern;
}

export interface IExternalRoutes {
  CART: string;
  SESSION_EXPIRED: string;
  SIGNED_OUT: string;
}

export interface IConfig {
  canCheckoutWithoutPayment: boolean;
  baseUrl: string;
  billingAddressInputSchema: IAddressSchemaProperties;
  billingAddressDisplaySchema: IAddressDisplaySchema;
  shippingAddressInputSchema: IAddressSchemaProperties;
  shippingAddressDisplaySchema: IAddressDisplaySchema;
  contactInfoInputSchema?: IAddressSchemaProperties;
  brandId: number;
  billingCountries?: ICountry[];
  shippingCountries?: ICountry[];
  convenienceStoreLimit?: number;
  packstationCountries?: ICountry[];
  brandName: string;
  regionId: number;
  regionName: string;
  cities: string[];
  locale: string;
  currency: string;
  countryId: number;
  messageOnlyTermsConditions: boolean;
  defaultTermsConditions: boolean;
  paymentInstructions: boolean;
  sessionTimeout: number;
  isSameDayDeliveryAvailable: boolean;
  isEstimatedDeliveryDateEnabled: boolean;
  isSplitShipmentEnabled: boolean;
  isMelissaEnabled: boolean;
  isProduction: boolean;
  vatPercentage: number;
  logoPath?: string;
  billingAddress?: ICountry[];
  shippingAddress?: ICountry[];
  states: IStates;
  backendValidation?: IBackendValidation;
  useUpsValidation: boolean;
  useCityStateLookup: boolean;
  useShippingCalculator: boolean;
  bopisEnabled: boolean;
  bopisGiftWrapEnabled: boolean;
  displayStoreCollection: boolean;
  isClickAndCollect: boolean;
  salonCommissionEnabled: boolean;
  isSalonDataShareEnabled: boolean;
  advancedLegal: boolean;
  displayDeliveryDate: boolean;
  displayIncludedTax: boolean;
  displayShippingFeeWithTax: boolean;
  countryTaxesLabel: boolean;
  tncContentNodeId: string;
  klarnaContentNodeId: string;
  giftWrap: boolean;
  condensedAddressEnabled: boolean;
  condensedFields?: string[];
  packstationAddressInputSchema: IAddressSchemaProperties;
  newsletter: boolean;
  showStrikeoffPrice: boolean;
  showFreeLabelAsPrice?: boolean;
  stardustServiceConfig: IStardustServiceConfig;
  // TODO: merge to billing options
  canSetCompanyBilling?: boolean;
  billToCompanySchema?: IAddressSchemaProperties;
  companyBillingDisplaySchema?: IAddressDisplaySchema;
  showInvoiceSection?: boolean;
  invoiceCarrierTypeOptions?: IInvoiceCarrierTypeOption[];
  invoiceDonationLoveCodeOptions?: IInvoiceDonationLoveCodeOption[];
  jsonSchemasEnabled: boolean;
  staticDataLookupUrls?: {
    stateCityUrl?: string;
    stateCityLockerUrl?: string;
  };
  displayOurGuaranteeOn: IDisplayOurGuaranteeConfig;
  maxGiftCardNumberLength?: number;
  privacyPolicyUrl?: string;
  googleMapsKey?: string;
  showStoreNameWithAddress?: boolean;
  externalRoutes?: IExternalRoutes;
  phonePatterns: IPhoneCountryPattern;
  useE164PhoneStandard: boolean;
  isCharityDonationEnabled: boolean;
  isWalletPaymentEnabled: boolean;
  useNewAddressDesign: boolean;
  googleMapStyle?: GOOGLE_MAP_STYLE;
}

class Config {
  @observable environment: EnvironmentType;

  @observable countryId: number | null = null;

  @observable brandName: string;

  @observable regionName: string;

  @observable messageOnlyTermsConditions = false;

  @observable defaultTermsConditions = false;

  @observable states: IStates = {};

  @observable cities: string[] = [];

  @observable paymentInstructions = false;

  @observable sessionTimeout: number;

  @observable currency = 'USD';

  @observable locale = 'en-US';

  @observable brandId: number | null = null;

  @observable regionId: number | null = null;

  @observable logoPath = '';

  @observable billingAddressInputSchema: IAddressSchemaProperties;

  // TODO: In the future this should be merged in the billingAddressInputSchema interface
  @observable billingAddressInputSchemaRequired: Array<keyof IAddressAttributes> = [];

  // TODO: In the future this should be merged in the billingAddressInputSchema interface
  @observable billingAddressInputSchemaDependentFields: Array<keyof IAddressAttributes>;

  @observable billingAddressDisplaySchema: IAddressDisplaySchema;

  @observable shippingAddressInputSchema: IAddressSchemaProperties;

  @observable shippingAddressDisplaySchema: IAddressDisplaySchema;

  @observable contactInfoInputSchema: IAddressSchemaProperties;

  @observable isProduction: boolean;

  @observable isSameDayDeliveryAvailable: boolean;

  @observable isEstimatedDeliveryDateEnabled = false;

  @observable isSplitShipmentEnabled: boolean;

  @observable convenienceStoreLimit?: number;

  @observable shippingCountries: ICountry[];

  @observable billingCountries: ICountry[];

  @observable packstationCountries: ICountry[];

  @observable vatPercentage: number;

  @observable baseUrl: string;

  @observable startPage: string;

  @observable canCheckoutWithoutPayment: boolean;

  @observable bopisEnabled: boolean;

  @observable bopisGiftWrapEnabled: boolean;

  @observable displayStoreCollection: boolean;

  @observable isClickAndCollect: boolean;

  @observable salonCommissionEnabled: boolean;

  @observable isSalonDataShareEnabled: boolean;

  @observable useUpsValidation: boolean;

  @observable backendValidation?: IBackendValidation;

  @observable useCityStateLookup: boolean;

  @observable useShippingCalculator: boolean;

  @observable advancedLegal: boolean;

  @observable tncContentNodeId: string;

  @observable klarnaContentNodeId: string;

  @observable displayDeliveryDate: boolean;

  @observable displayIncludedTax: boolean;

  @observable displayShippingFeeWithTax: boolean;

  @observable countryTaxesLabel: boolean;

  @observable showStoreNameWithAddress: boolean;

  @observable condensedAddressEnabled: boolean;

  @observable condensedFields: string[];

  @observable packstationAddressInputSchema: IAddressSchemaProperties;

  @observable showStrikeoffPrice: boolean;

  @observable showFreeLabelAsPrice: boolean;

  @observable newsletter: boolean;

  @observable stardustServiceConfig: IStardustServiceConfig;

  @observable canSetCompanyBilling: boolean;

  @observable billToCompanySchema: IAddressSchemaProperties;

  @observable companyBillingDisplaySchema: IAddressDisplaySchema;

  @observable showInvoiceSection: boolean;

  @observable invoiceCarrierTypeOptions: IInvoiceCarrierTypeOption[];

  @observable invoiceDonationLoveCodeOptions: IInvoiceCarrierTypeOption[];

  @observable jsonSchemasEnabled: boolean;


  @observable staticDataLookupUrls?: Partial<Record<'stateCityUrl' | 'stateCityLockerUrl', string>>;

  @observable payPalSettings: IPayPalSettings;

  @observable displayOurGuaranteeOn: IDisplayOurGuaranteeConfig;

  @observable maxGiftCardNumberLength?: number;

  @observable privacyPolicyUrl?: string;

  @observable googleMapsKey: string;

  @observable isMelissaEnabled = false;

  @observable isMelissaExperimentEnabled = false;

  @observable isGiftWrapAndDonationsHidingExperimentEnabled = false;

  @observable phonePatterns: IPhoneCountryPattern = {};

  @observable externalRoutes: IExternalRoutes;

  @observable useE164PhoneStandard = false;

  @observable isCharityDonationEnabled = false;

  @observable isWalletPaymentEnabled = false;

  @observable useNewAddressDesign = false;

  @observable googleMapStyle = GOOGLE_MAP_STYLE.DEFAULT;

  @observable displayConfig: DisplayConfig;

  static fromJSON(data: IConfig): Config {
    const config = new Config();
    set(config, {
      ...data,
      environment: getEnvironment(data.isProduction),
    });
    return config;
  }

  @computed
  public get supportsTypeaheadInput(): boolean {
    return this.isMelissaEnabled && this.isMelissaExperimentEnabled;
  }

  @action
  updateConfig = (config: Config): void => {
    set(this, config);
  };

  getStates = (countryId: number): IState[] => {
    if (this.states && this.states[countryId]) {
      return this.states[countryId].filter(s => Boolean(s.value));
    }

    return [];
  };

  @action
  getBackendValidateAddressConfig = (countryId: number): IBackendValidation => {
    const { backendValidation } = this;
    // if backend validation config has country id, then only return the config when it is the same
    // as the selected country id
    // if backend validation config has no country id, then this config should be applied to all countries
    if (backendValidation?.countryId) {
      return backendValidation.countryId === countryId && backendValidation;
    } else {
      return backendValidation;
    }
  };
}

export default Config;
